import React, { useState, useMemo } from 'react';
import AuditDiffSelectNav from './AuditDiffSelectNav';
import TaskPreview from 'react-scale/corp/components/Lookup/sections/TaskPreview';
import { AuditPerAnnotation } from '@scale/shared/types/props/audit';
import _ from 'lodash';

import { TaskType } from '@scale/shared-types';

interface MultiResponseItem {
  name: string;
  getTaskProps: () => MultiResponseTaskProps;
  isDefault?: boolean;
}

interface MultiResponseTaskProps {
  fixResponse: any;
  comments?: string;
  auditPerAnnotation?: Pick<AuditPerAnnotation, 'readOnly' | 'perAnnotationErrors'>;
  attachments?: string[];
}

function generateMultiResponseItems(object: $FIXME): MultiResponseItem[] {
  const items: MultiResponseItem[] = [];

  if (object.params.hypothesis) {
    items.push({
      name: 'Prelabels',
      getTaskProps: () => ({ fixResponse: object.params.hypothesis }),
    });
  }

  const isModel = object.type === TaskType.DocumentModel;

  items.push({
    name: isModel ? 'Model Results' : 'Last Worker Response',
    getTaskProps: () => ({
      fixResponse:
        _.size(object.customerAudits) > 0
          ? object.customerAudits[0].auditedResponse
          : object.response,
    }),
    isDefault: isModel || _.isEmpty(object.customerAudits),
  });

  if (object.personalDataProtection?.compliantAttachments) {
    items.push({
      name: 'Compliant Response',
      getTaskProps: () => ({
        fixResponse: object.personalDataProtection.compliantResponse,
        attachments: object.personalDataProtection.compliantAttachments.map(
          (ca: { s3URL: string }) => ca.s3URL,
        ),
      }),
    });
  }

  _.forEach(object.customerAudits, (customerAudit, i) => {
    const { perAnnotationErrors, customerUpdatedResponse, comments } = customerAudit;
    items.push({
      name: `Audit v${i}`,
      getTaskProps: () => {
        const errorIds = perAnnotationErrors.reduce((ids: Set<string>, pae: $FIXME) => {
          ids.add(pae.annotationId);
          return ids;
        }, new Set<string>());

        const annotations =
          customerUpdatedResponse?.annotations.filter((a: any) => errorIds.has(a.uuid)) ?? [];

        return {
          auditPerAnnotation: {
            readOnly: true,
            perAnnotationErrors,
          },
          fixResponse: {
            annotations,
          },
          comments,
        };
      },
    });
  });

  if (_.size(object.customerAudits) > 0) {
    items.push({
      name: `Final: ${isModel ? 'Model' : 'Labeling'} & Audits`,
      getTaskProps: () => {
        const { perAnnotationErrors, customerUpdatedResponse, auditedResponse, comments } =
          object.customerAudits.slice(-1)[0];
        return {
          auditPerAnnotation: {
            readOnly: true,
            perAnnotationErrors,
          },
          fixResponse: customerUpdatedResponse ?? auditedResponse,
          comments,
        };
      },
      isDefault: !isModel,
    });
  }
  return items;
}

const AuditDiffTaskView: React.FC<{
  object: $FIXME;
  config: $FIXME;
}> = ({ object, config }) => {
  const multiResponseItems = useMemo(() => {
    return generateMultiResponseItems(object);
  }, [object]);

  const [itemIndex, setItemIndex] = useState(() => {
    return _.findIndex(multiResponseItems, { isDefault: true }) ?? 0;
  });

  const taskProps = useMemo(() => {
    return multiResponseItems[itemIndex].getTaskProps();
  }, [multiResponseItems, itemIndex]);

  const withReplacedResponse = {
    ...object,
    auditPerAnnotation: taskProps.auditPerAnnotation,
  };

  if (taskProps.attachments) {
    withReplacedResponse.params = {
      ...object.params,
      attachments: taskProps.attachments,
    };
  }

  return (
    <div>
      <AuditDiffSelectNav
        view={itemIndex}
        views={multiResponseItems.map(item => item.name)}
        setView={setItemIndex}
      />
      <div style={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
        <TaskPreview
          object={withReplacedResponse}
          config={config}
          response={taskProps.fixResponse}
        />
      </div>
      <div>
        <h4>Comments:</h4>
        <div>{taskProps.comments ?? 'N/A'}</div>
      </div>
    </div>
  );
};

export default AuditDiffTaskView;
